import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Entity, EntityType, PaginationInfo } from '../types';
import type { ResolvedEntityMap } from '../utils/resolveColumns';
import type { ColumnDef } from '../config/viewsConfig';
import { renderCellValue, getCellPlainValue } from '../utils/renderCellValue';
import { PaginationControls } from './PaginationControls';
import { ColumnPicker } from './ColumnPicker';
import { useEditMode } from '../context/EditModeContext';
import './EntityTable.css';

interface EntityTableProps {
  entities: Entity[];
  entityType?: EntityType;
  columns: ColumnDef[];
  defaultColumns?: ColumnDef[];
  onColumnsChange?: (columns: ColumnDef[]) => void;
  resolvedEntities?: ResolvedEntityMap;
  pagination: PaginationInfo;
  onPageChange: (offset: number) => void;
  onLimitChange: (limit: number) => void;
  onEntityClick?: (entityId: number) => void;
  onSortChange?: (sortBy: string) => void;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  loading?: boolean;
  /** Override the filename stem for CSV export (e.g. 'nmr-tests'). */
  exportName?: string;
  /** When client-side search is active, the number of entities that matched. */
  filteredCount?: number;
  /** Raw entities for JSON key sampling in the column picker. Falls back to entities prop. */
  sampleEntities?: Entity[];
  onDeleteEntity?: (entityId: number) => void;
}

export const EntityTable: React.FC<EntityTableProps> = ({
  entities,
  entityType,
  columns,
  defaultColumns,
  onColumnsChange,
  resolvedEntities = new Map(),
  pagination,
  onPageChange,
  onLimitChange,
  onEntityClick,
  onSortChange,
  sortBy,
  sortOrder = 'asc',
  loading = false,
  exportName,
  filteredCount,
  sampleEntities,
  onDeleteEntity,
}) => {
  const navigate = useNavigate();
  const { isEditMode } = useEditMode();
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const getSortKey = (col: ColumnDef): string => col.key;

  const handleSortClick = (col: ColumnDef) => {
    if (onSortChange) {
      onSortChange(getSortKey(col));
    }
  };

  const renderSortIndicator = (col: ColumnDef) => {
    if (sortBy !== getSortKey(col)) {
      return <span className="sort-indicator">↕</span>;
    }
    return (
      <span className={`sort-indicator sort-${sortOrder}`}>
        {sortOrder === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  const handleExportCsv = useCallback(() => {
    const escapeCsv = (val: string) => {
      if (val.includes(',') || val.includes('"') || val.includes('\n')) {
        return `"${val.replace(/"/g, '""')}"`;
      }
      return val;
    };

    const headerRow = columns.map(col => escapeCsv(col.label || col.key)).join(',');
    const dataRows = entities.map(entity =>
      columns.map(col => escapeCsv(getCellPlainValue(entity, col, resolvedEntities))).join(',')
    );

    const csv = [headerRow, ...dataRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const stem = exportName || entityType || 'entities';
    a.download = `export-${stem}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [columns, entities, resolvedEntities, entityType, exportName]);

  const cellOptions = { onEntityClick, onNavigate: navigate, resolvedEntities };

  if (loading) {
    return (
      <div className="table-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (entities.length === 0) {
    return (
      <div className="table-empty">
        <p>No entities found</p>
      </div>
    );
  }

  return (
    <div className="entity-table-container">
      <div className="table-controls">
        <div className="pagination-info">
          {filteredCount !== undefined
            ? `Showing ${filteredCount} of ${entities.length} on this page (${pagination.count} total)`
            : `Showing ${pagination.offset + 1}\u2013${Math.min(pagination.offset + pagination.limit, pagination.count)} of ${pagination.count}`
          }
        </div>
        <div className="table-controls-right">
          <div className="limit-control">
            <label>
              Per page:
              <select
                value={pagination.limit}
                onChange={(e) => onLimitChange(parseInt(e.target.value, 10))}
                className="limit-select"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </select>
            </label>
          </div>
          {entityType && defaultColumns && onColumnsChange && (
            <ColumnPicker
              entityType={entityType}
              defaultColumns={defaultColumns}
              visibleColumns={columns}
              onColumnsChange={onColumnsChange}
              entities={sampleEntities || entities}
            />
          )}
          <button className="column-picker-btn" onClick={handleExportCsv} title="Export CSV">
            Export CSV
          </button>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="entity-table">
          <thead>
            <tr>
              {isEditMode && onDeleteEntity && <th className="actions-col"></th>}
              {columns.map(col => {
                const isSortable = col.sortable !== false && !col.extract;
                return (
                  <th
                    key={col.key}
                    className={`${onSortChange && isSortable ? 'sortable' : ''} ${sortBy === getSortKey(col) ? 'sorted' : ''}`}
                    onClick={onSortChange && isSortable ? () => handleSortClick(col) : undefined}
                  >
                    {col.label} {onSortChange && isSortable && renderSortIndicator(col)}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {entities.map((entity) => (
              <tr key={entity.id}>
                {isEditMode && onDeleteEntity && (
                  <td className="actions-cell">
                    {confirmDeleteId === entity.id ? (
                      <span className="confirm-delete">
                        <button className="confirm-yes" onClick={() => { onDeleteEntity(entity.id!); setConfirmDeleteId(null); }}>Yes</button>
                        <button className="confirm-no" onClick={() => setConfirmDeleteId(null)}>No</button>
                      </span>
                    ) : (
                      <button className="delete-row-btn" title="Delete" onClick={() => setConfirmDeleteId(entity.id!)}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                      </button>
                    )}
                  </td>
                )}
                {columns.map(col => (
                  <td key={col.key}>{renderCellValue(entity, col, cellOptions)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PaginationControls
        offset={pagination.offset}
        limit={pagination.limit}
        count={pagination.count}
        onPageChange={onPageChange}
        disabled={loading}
      />
    </div>
  );
};
